<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <section class="hero-section relative h-screen flex items-center justify-center overflow-hidden">
      <!-- Background Video -->
      <div class="absolute inset-0 w-full h-full">
        <!-- Fallback background - shows if video fails -->
        <div 
          ref="fallbackBackgroundRef"
          class="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 z-0"
        ></div>
        <!-- Video background -->
        <video 
          ref="backgroundVideoRef"
          :src="backgroundVideo"
          autoplay
          loop
          muted
          playsinline
          preload="auto"
          class="absolute inset-0 w-full h-full object-cover z-10"
          @loadedmetadata="handleVideoLoaded"
          @error="handleVideoError"
        ></video>
        <!-- Overlay for better text readability -->
        <div class="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50 z-20"></div>
      </div>
      
      <!-- Content Container -->
      <div class="relative z-20 text-center px-4 max-w-6xl mx-auto">
        <h1 class="text-3xl md:text-5xl font-heading tracking-tight mb-6">
          <span class="block text-white font-light">{{ translations.hero.titlePart1 }}</span>
          <span class="block font-bold bg-gradient-to-r from-white via-emerald-300 to-white bg-clip-text text-transparent">{{ translations.hero.titlePart2 }}</span>
        </h1>
        
        <p class="text-xl md:text-2xl text-white max-w-3xl mx-auto mb-8 leading-relaxed">
          {{ translations.hero.subtitle }}
        </p>
      </div>
    </section>

    <!-- Contact Form Section -->
    <div class="relative bg-gray-900">
      <section class="py-24">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Contact Form -->
            <div class="bg-gray-800 rounded-2xl p-8 shadow-lg">
              <h2 class="text-3xl md:text-4xl font-heading tracking-tight mb-6 font-bold text-white">{{ translations.form.title }}</h2>
              
              <!-- Success Message -->
              <div v-if="success" class="mb-6 p-4 bg-green-900/50 text-green-300 rounded-lg border border-green-700">
                {{ translations.form.success }}
              </div>
              
              <!-- Error Message -->
              <div v-if="error" class="mb-6 p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700">
                {{ error }}
              </div>

              <form @submit.prevent="handleSubmit" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label for="name" class="block text-sm font-medium text-gray-300 mb-2">{{ translations.form.name }}</label>
                    <input type="text" id="name" v-model="form.name" required
                           class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary">
                    <div v-if="fieldErrors.name" class="text-red-400 text-sm mt-1">{{ fieldErrors.name }}</div>
                  </div>
                  <div>
                    <label for="email" class="block text-sm font-medium text-gray-300 mb-2">{{ translations.form.email }}</label>
                    <input type="email" id="email" v-model="form.email" required
                           class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary">
                    <div v-if="fieldErrors.email" class="text-red-400 text-sm mt-1">{{ fieldErrors.email }}</div>
                  </div>
                </div>
                <div>
                  <label for="subject" class="block text-sm font-medium text-gray-300 mb-2">{{ translations.form.subject }}</label>
                  <input type="text" id="subject" v-model="form.subject" required
                         class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary">
                  <div v-if="fieldErrors.subject" class="text-red-400 text-sm mt-1">{{ fieldErrors.subject }}</div>
                </div>
                <div>
                  <label for="serviceType" class="block text-sm font-medium text-gray-300 mb-2">{{ translations.form.serviceType || 'What type of services' }}</label>
                  <select id="serviceType" v-model="form.serviceType" required
                          class="w-full px-4 py-3 rounded-lg border border-gray-600 focus:ring-2 focus:ring-primary focus:border-primary bg-gray-700 text-gray-100">
                    <option value="">{{ translations.form.selectService || 'Select a service' }}</option>
                    <option v-for="service in availableServices" :key="service.key" :value="service.title">
                      {{ service.title }}
                    </option>
                  </select>
                  <div v-if="fieldErrors.serviceType" class="text-red-400 text-sm mt-1">{{ fieldErrors.serviceType }}</div>
                  <!-- Debug: Show services count -->
                  <div v-if="availableServices.length === 0" class="text-yellow-400 text-sm mt-1">
                    No services loaded. Using fallback.
                  </div>
                </div>
                <div>
                  <label for="message" class="block text-sm font-medium text-gray-300 mb-2">{{ translations.form.message }}</label>
                  <textarea id="message" v-model="form.message" rows="6" required
                            class="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary"></textarea>
                  <div v-if="fieldErrors.message" class="text-red-400 text-sm mt-1">{{ fieldErrors.message }}</div>
                </div>
                <button type="submit" 
                        :disabled="isLoading"
                        class="w-full inline-flex items-center justify-center px-6 py-3 text-lg font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                  <span v-if="isLoading">{{ translations.form.submitting }}</span>
                  <span v-else>{{ translations.form.submit }}</span>
                </button>
              </form>
            </div>

            <!-- Contact Information -->
            <div class="space-y-8">
              <div>
                <h2 class="text-3xl md:text-4xl font-heading tracking-tight mb-6 font-bold text-white">{{ translations.info.title }}</h2>
                <p class="text-gray-300 mb-6">
                  {{ translations.info.description }}
                </p>
              </div>

              <div class="space-y-6">
                <!-- Address commented out
                <div class="flex items-start">
                  <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <div class="ml-4">
                    <h3 class="text-lg font-semibold text-gray-100">{{ translations.info.address.label }}</h3>
                    <p class="text-gray-300">{{ translations.info.address.value }}</p>
                  </div>
                </div>
                -->

                <div class="flex items-start">
                  <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                  </div>
                  <div class="ml-4">
                    <h3 class="text-lg font-semibold text-gray-100">{{ translations.info.phone.label }}</h3>
                    <p class="text-gray-300">{{ translations.info.phone.value }}</p>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <div class="ml-4">
                    <h3 class="text-lg font-semibold text-gray-100">{{ translations.info.officeAddress.label }}</h3>
                    <p class="text-gray-300">{{ translations.info.officeAddress.value }}</p>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div class="ml-4">
                    <h3 class="text-lg font-semibold text-gray-100">{{ translations.info.email.label }}</h3>
                    <p class="text-gray-300">{{ translations.info.email.value }}</p>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <svg class="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div class="ml-4">
                    <h3 class="text-lg font-semibold text-gray-100">{{ translations.info.whatsapp.label }}</h3>
                    <a :href="`https://wa.me/${translations.info.whatsapp.value.replace(/\s/g, '').replace(/\+/g, '')}`" target="_blank" rel="noopener noreferrer" class="text-gray-300">{{ translations.info.whatsapp.value }}</a>
                  </div>
                </div>
              </div>

              <!-- Business Hours -->
              <div class="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 class="text-lg font-semibold mb-4 font-bold text-white">{{ translations.businessHours.title }}</h3>
                <div class="space-y-2">
                  <div v-for="(day, index) in translations.businessHours.days" :key="index" 
                       class="flex justify-between">
                    <span class="text-gray-300">{{ day.name }}</span>
                    <span class="font-medium text-gray-100">{{ day.hours }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Map Section -->
      <section class="h-[400px] bg-gray-800">
        <div class="h-full w-full">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2595.0000000000005!2d8.672783!3d49.412081!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDnCsDI0JzQzLjUiTiA4wrA0MCMyMi4wIkU!5e0!3m2!1sde!2sde!4v1234567890"
            class="w-full h-full border-0"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade">
          </iframe>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'
import backgroundVideo from '../assets/6012903_People_Person_1920x1080.mp4'

const backgroundVideoRef = ref(null)
const fallbackBackgroundRef = ref(null)

// Try to use i18n, but make it optional with fallback
let t = null
let locale = ref('en')
try {
  const i18n = useI18n()
  t = i18n.t
  locale = i18n.locale
} catch (e) {
  t = (key) => key // Return key as fallback
  locale = ref('en')
}

// Fallback translations (always available)
const fallbackTranslations = {
  hero: {
    titlePart1: 'Focused Listening.',
    titlePart2: 'Better Solutions',
    subtitle: 'Through concentrated listening, we turn your concerns into solutions.'
  },
  form: {
    title: 'Send Us a Message',
    name: 'Name',
    email: 'Email',
    subject: 'Subject',
    serviceType: 'What type of services',
    selectService: 'Select a service',
    message: 'Message',
    submit: 'Send Message',
    submitting: 'Sending...',
    success: 'Your message has been sent successfully. We will get back to you shortly.'
  },
  info: {
    title: 'Contact Information',
    description: 'Our team is available for all your questions. We respond within 24 hours.',
    address: {
      label: 'Address',
      value: 'Jahnstrasse 32, D-69120 Heidelberg, Germany'
    },
    officeAddress: {
      label: 'Office Address',
      value: 'Kurfuersten-Anlage 52, 69115 Heidelberg, Germany'
    },
    phone: {
      label: 'Phone',
      value: '+49  6221 6470309'
    },
    email: {
      label: 'Email',
      value: 'connect@heidelbell.com'
    },
    whatsapp: {
      label: 'WhatsApp',
      value: '+49 176 47050093'
    }
  },
  businessHours: {
    title: 'Business Hours',
    days: [
      { name: 'Monday - Friday', hours: '09:00 - 18:00' },
      { name: 'Saturday', hours: 'Closed' },
      { name: 'Sunday', hours: 'Closed' }
    ]
  }
}

// Safe translation helper
const safeT = (key, fallback) => {
  try {
    if (t && typeof t === 'function') {
      const result = t(key)
      return result && result !== key ? result : fallback
    }
    return fallback
  } catch (e) {
    return fallback
  }
}

// Use computed properties for translations with error handling
const translations = computed(() => {
  try {
    return {
      hero: {
        titlePart1: safeT('contact.hero.titlePart1', fallbackTranslations.hero.titlePart1),
        titlePart2: safeT('contact.hero.titlePart2', fallbackTranslations.hero.titlePart2),
        subtitle: safeT('contact.hero.subtitle', fallbackTranslations.hero.subtitle)
      },
      form: {
        title: safeT('contact.form.title', fallbackTranslations.form.title),
        name: safeT('contact.form.name', fallbackTranslations.form.name),
        email: safeT('contact.form.email', fallbackTranslations.form.email),
        subject: safeT('contact.form.subject', fallbackTranslations.form.subject),
        serviceType: safeT('contact.form.serviceType', fallbackTranslations.form.serviceType),
        selectService: safeT('contact.form.selectService', fallbackTranslations.form.selectService),
        message: safeT('contact.form.message', fallbackTranslations.form.message),
        submit: safeT('contact.form.submit', fallbackTranslations.form.submit),
        submitting: safeT('contact.form.submitting', fallbackTranslations.form.submitting),
        success: safeT('contact.form.success', fallbackTranslations.form.success)
      },
      info: {
        title: safeT('contact.info.title', fallbackTranslations.info.title),
        description: safeT('contact.info.description', fallbackTranslations.info.description),
        address: {
          label: safeT('contact.info.address.label', fallbackTranslations.info.address.label),
          value: safeT('contact.info.address.value', fallbackTranslations.info.address.value)
        },
        phone: {
          label: safeT('contact.info.phone.label', fallbackTranslations.info.phone.label),
          value: safeT('contact.info.phone.value', fallbackTranslations.info.phone.value)
        },
        email: {
          label: safeT('contact.info.email.label', fallbackTranslations.info.email.label),
          value: safeT('contact.info.email.value', fallbackTranslations.info.email.value)
        },
        officeAddress: {
          label: safeT('contact.info.officeAddress.label', fallbackTranslations.info.officeAddress.label),
          value: safeT('contact.info.officeAddress.value', fallbackTranslations.info.officeAddress.value)
        },
        whatsapp: {
          label: safeT('contact.info.whatsapp.label', fallbackTranslations.info.whatsapp.label),
          value: safeT('contact.info.whatsapp.value', fallbackTranslations.info.whatsapp.value)
        }
      },
      businessHours: {
        title: safeT('contact.businessHours.title', fallbackTranslations.businessHours.title),
        days: (() => {
          try {
            if (t && typeof t === 'function') {
              const days = t('contact.businessHours.days')
              if (Array.isArray(days) && days.length > 0) {
                return days
              }
            }
          } catch (e) {
            // Use fallback
          }
          return fallbackTranslations.businessHours.days
        })()
      }
    }
  } catch (e) {
    return fallbackTranslations
  }
})

// Form state
const form = ref({
  name: '',
  email: '',
  subject: '',
  serviceType: '',
  message: ''
})

// Get available services from translations
const availableServices = computed(() => {
  try {
    const services = []
    const currentLocale = locale.value || 'en'
    // Handle both default export and direct import
    const enMessages = enTranslations.default || enTranslations
    const deMessages = deTranslations.default || deTranslations
    const messages = currentLocale === 'en' ? enMessages : deMessages
    
    // Get services from servicesList
    const servicesList = messages?.services?.servicesList
    if (servicesList && typeof servicesList === 'object') {
      Object.keys(servicesList).forEach(key => {
        const service = servicesList[key]
        if (service && typeof service === 'object' && service.title) {
          services.push({ key, title: service.title })
        }
      })
    }
    
    // Get services from items
    const items = messages?.services?.items
    if (items && typeof items === 'object') {
      Object.keys(items).forEach(key => {
        const service = items[key]
        if (service && typeof service === 'object' && service.title) {
          services.push({ key, title: service.title })
        }
      })
    }
    
    // Get service models
    const serviceModels = messages?.services?.serviceModels
    if (serviceModels && typeof serviceModels === 'object') {
      if (serviceModels.flatRate && serviceModels.flatRate.title) {
        services.push({ key: 'flatRate', title: serviceModels.flatRate.title })
      }
      if (serviceModels.activityBased && serviceModels.activityBased.title) {
        services.push({ key: 'activityBased', title: serviceModels.activityBased.title })
      }
    }
    
    // Fallback services if translations fail
    if (services.length === 0) {
      console.warn('No services found in translations, using fallback')
      return [
        { key: 'cloud', title: 'Cloud Solutions' },
        { key: 'resourceTeams', title: 'Resource or Teams as Services' },
        { key: 'itSupport', title: 'IT Support' },
        { key: 'optimization', title: 'Process Optimization' },
        { key: 'infrastructure', title: 'On Demand Infrastructure Services' },
        { key: 'project', title: 'Project Management Support' },
        { key: 'data', title: 'Data Management and Analytics' },
        { key: 'dispatch', title: 'Resource or Team as Service' },
        { key: 'flatRate', title: 'Flat-Rate Basic Health Check Service' },
        { key: 'activityBased', title: 'Activity-Based Customised Service' }
      ]
    }
    
    return services
  } catch (e) {
    console.error('Error loading services:', e)
    // Return fallback services on error
    return [
      { key: 'cloud', title: 'Cloud Solutions' },
      { key: 'resourceTeams', title: 'Resource or Teams as Services' },
      { key: 'itSupport', title: 'IT Support' },
      { key: 'optimization', title: 'Process Optimization' },
      { key: 'infrastructure', title: 'On Demand Infrastructure Services' },
      { key: 'project', title: 'Project Management Support' },
      { key: 'data', title: 'Data Management and Analytics' },
      { key: 'dispatch', title: 'Resource or Team as Service' },
      { key: 'flatRate', title: 'Flat-Rate Basic Health Check Service' },
      { key: 'activityBased', title: 'Activity-Based Customised Service' }
    ]
  }
})

const isLoading = ref(false)
const error = ref(null)
const success = ref(false)
const fieldErrors = ref({})

// Simple form handler
const handleSubmit = async () => {
  isLoading.value = true
  error.value = null
  success.value = false
  fieldErrors.value = {}

  // Simple validation
  if (!form.value.name || form.value.name.trim().length < 2) {
    fieldErrors.value.name = 'Name is required'
    isLoading.value = false
    return
  }

  if (!form.value.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    fieldErrors.value.email = 'Valid email is required'
    isLoading.value = false
    return
  }

  if (!form.value.subject || form.value.subject.trim().length < 3) {
    fieldErrors.value.subject = 'Subject is required'
    isLoading.value = false
    return
  }

  if (!form.value.serviceType || form.value.serviceType.trim().length === 0) {
    fieldErrors.value.serviceType = 'Please select a service type'
    isLoading.value = false
    return
  }

  if (!form.value.message || form.value.message.trim().length < 10) {
    fieldErrors.value.message = 'Message is required (min 10 characters)'
    isLoading.value = false
    return
  }

  // Simulate form submission
  try {
    await new Promise(resolve => setTimeout(resolve, 1000))
    success.value = true
    form.value = { name: '', email: '', subject: '', serviceType: '', message: '' }
    setTimeout(() => { success.value = false }, 5000)
  } catch (err) {
    error.value = 'An error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}

const handleVideoLoaded = () => {
  // Hide fallback when video loads successfully
  if (fallbackBackgroundRef.value) {
    fallbackBackgroundRef.value.style.display = 'none'
  }
  if (backgroundVideoRef.value) {
    backgroundVideoRef.value.play().catch(() => {
      // Video autoplay prevented - show fallback
      if (fallbackBackgroundRef.value) {
        fallbackBackgroundRef.value.style.display = 'block'
      }
    })
  }
}

const handleVideoError = () => {
  // Show fallback background when video fails
  if (fallbackBackgroundRef.value) {
    fallbackBackgroundRef.value.style.display = 'block'
  }
}

onMounted(() => {
  // Ensure video plays when mounted
  if (backgroundVideoRef.value) {
    // Set video source explicitly if needed
    if (backgroundVideoRef.value.src !== backgroundVideo) {
      backgroundVideoRef.value.src = backgroundVideo
    }
    const playPromise = backgroundVideoRef.value.play()
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Show fallback if autoplay fails
        if (fallbackBackgroundRef.value) {
          fallbackBackgroundRef.value.style.display = 'block'
        }
      })
    }
  }
})
</script>
